import React, { useState } from 'react';
import { Eye } from 'lucide-react';
import { Button } from './ui/button';

export const ProductGrid = ({ products, onSelectProduct }) => {
  if (!products?.length) {
    return <div className="p-8 text-center text-neutral-500">No products in this category. Add some from admin.</div>;
  }
  return (
    <div className="px-3 md:px-6 py-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
        {products.map((p) => {
          const cover = p.photos?.[0] || 'https://via.placeholder.com/400x400?text=No+Image';
          return (
            <div key={p.id} className="group border border-neutral-200 rounded-lg overflow-hidden bg-white hover:shadow-lg hover:border-orange-300 transition-all">
              <div className="aspect-square bg-neutral-100 overflow-hidden relative">
                <img src={cover} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                <span data-testid={`product-${p.id}-colour-stock-badge`} className="absolute top-2 left-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sky-600 text-white shadow">Colour-wise stock</span>
              </div>
              <div className="p-3">
                <h3 className="font-semibold text-[13px] text-neutral-900 line-clamp-1">{p.name}</h3>
                <p className="text-[11px] text-neutral-500 mt-0.5 line-clamp-2 min-h-[28px]">{p.description}</p>
                <div className="flex items-baseline gap-1 mt-2">
                  <span className="text-orange-600 font-bold text-[15px]">₹{p.price_high}</span>
                  <span className="text-[10px] text-neutral-400 line-through">₹{p.price_low}</span>
                  <span className="text-[10px] text-neutral-500 ml-auto">Colour-wise MOQ</span>
                </div>
                <div className="flex gap-1 mt-2">
                  {(p.colors || []).slice(0, 6).map((c, i) => (
                    <span key={i} className="w-3.5 h-3.5 rounded-full border border-neutral-300" style={{ background: c.hex }} title={c.name} />
                  ))}
                  {(p.colors?.length || 0) > 6 && <span className="text-[10px] text-neutral-500">+{p.colors.length - 6}</span>}
                </div>
                <Button
                  data-testid={`product-${p.id}-select-colours-button`}
                  size="sm"
                  onClick={() => onSelectProduct(p.id)}
                  className="w-full mt-2 h-7 text-[11px] bg-orange-500 hover:bg-orange-600"
                >
                  <Eye className="w-3 h-3 mr-1" /> Select Colours & Qty
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ProductList = ({ products, onSelectProduct }) => {
  if (!products?.length) {
    return <div className="p-8 text-center text-neutral-500">No products in this category. Add some from admin.</div>;
  }
  return (
    <div className="px-3 md:px-6 py-4">
      <div className="space-y-2">
        {products.map((p) => {
          const cover = p.photos?.[0] || 'https://via.placeholder.com/200x200?text=No+Image';
          return (
            <div key={p.id} className="flex gap-3 p-3 border border-neutral-200 rounded-lg bg-white hover:border-orange-300 hover:shadow-sm transition-all">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded overflow-hidden bg-neutral-100 shrink-0">
                <img src={cover} alt={p.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-[14px] text-neutral-900">{p.name}</h3>
                  <span data-testid={`product-${p.id}-colour-stock-badge`} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-800 border border-sky-200">Colour-wise stock</span>
                </div>
                <p className="text-[12px] text-neutral-600 line-clamp-1">{p.description}</p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-orange-600 font-bold text-[14px]">₹{p.price_high}</span>
                  <span className="text-[11px] text-neutral-400 line-through">₹{p.price_low}</span>
                  <span className="text-[11px] px-1.5 py-0.5 bg-neutral-100 rounded text-neutral-600">Colour-wise MOQ</span>
                  <span className="text-[11px] text-neutral-500">· {(p.sizes || []).join(', ')}</span>
                </div>
                <div className="flex gap-1 mt-1.5">
                  {(p.colors || []).slice(0, 10).map((c, i) => (
                    <span key={i} className="w-3.5 h-3.5 rounded-full border border-neutral-300" style={{ background: c.hex }} title={c.name} />
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1 justify-center">
                <Button data-testid={`product-${p.id}-select-colours-button`} size="sm" onClick={() => onSelectProduct(p.id)} className="h-7 text-[11px] bg-orange-500 hover:bg-orange-600 whitespace-nowrap">
                  <Eye className="w-3 h-3 mr-1" /> Select Colours
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
